import { useInView } from 'react-intersection-observer';
import { Link, useOutletContext } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';

import { useActiveAnalysis } from '../services/api/KeynomeAPIHooks';
import { getSampleDisplayState } from '../AnalysisStatus/analysisStatusUtils';
import { formatEstimatedTime } from '../utils';

function SampleInDatasetListItem(props) {
  const {
    project,
    selectedRun,
    sample,
    isIllumina,
  } = props;

  const { ref, inView } = useInView();
  const { hasAccessReportViruses } = useOutletContext();

  const { data: activeAnalysis } = useActiveAnalysis(
    sample.id,
    {
      queryParams: { extraFields: ['status', 'positive_species_calls_ct', 'estimated_minutes_to_result'] },
      useQueryOptions: {refetchInterval: 60000}, // refetch every minute
      fetchEnabled: inView,                      // only fetch when sample is in view
    }
  );

  const sampleDisplayState = getSampleDisplayState({
    primaryAnalysisStatus: activeAnalysis?.attributes?.status,
  });
  let statusLabel = sampleDisplayState.label;
  if (sampleDisplayState.key === 'analysis_running') {
    const eta = formatEstimatedTime(activeAnalysis.attributes.estimated_minutes_to_result);
    statusLabel = eta ? `Analyzing — ${eta} remaining` : 'Analyzing';
  }
  let statusContent = <CircularProgress size={20} sx={{ mt: 2 }} />;
  let isClickable = sampleDisplayState.isClickable;
  statusContent = (<p style={{ color: sampleDisplayState.color }}><b>{statusLabel}</b></p>);

  let yieldContent = <p>-</p>;
  if (activeAnalysis && activeAnalysis.attributes.raw_total_megabases && selectedRun) {
    const yieldValue = isIllumina ? (activeAnalysis.attributes.raw_total_megabases + activeAnalysis.attributes.raw_total_megabases_r2) : activeAnalysis.attributes.raw_total_megabases

    const yieldAsFormattedString = yieldValue.toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    );

    yieldContent = (
      <p>
        {yieldAsFormattedString}
        {' '}
        MBs
      </p>
    );
  }

  let speciesIDContent = <p>-</p>;
  if (activeAnalysis) {
    let speciesIDCall = '-';
    let pluralityOfSpeciesIDCall = null;
    if (activeAnalysis.attributes.status === 'succeeded') {
        speciesIDCall = (!selectedRun || (selectedRun.attributes.sequencing_platform === 'illumina' || selectedRun.attributes.platform === 'illumina')) ? '-' : 'Negative'

        let positive_species_calls_ct = activeAnalysis.attributes.positive_species_calls_ct
        if (!hasAccessReportViruses && activeAnalysis.attributes?.positive_species_calls_cts_by_domain) {
          positive_species_calls_ct -= activeAnalysis.attributes.positive_species_calls_cts_by_domain['virus'];
        }

        if (positive_species_calls_ct > 0) {
          speciesIDCall = 'Positive';

          pluralityOfSpeciesIDCall = (positive_species_calls_ct === 1)
            ? ' (monomicrobial)' : ' (polymicrobial)';
        }

    }

    speciesIDContent = (
      <p style={{margin: pluralityOfSpeciesIDCall ? "5px" : null}}>
        <b>
            <small>
          {speciesIDCall}
          {pluralityOfSpeciesIDCall ? <br /> : null}
          {pluralityOfSpeciesIDCall}</small>
        </b>
      </p>
    );
  }

  return (
    <Link ref={ref} to={isClickable ? `/projects/${project?.attributes.uuid}/runs/${selectedRun.attributes.uuid}/samples/${sample?.attributes.uuid}` : null} style={{ textDecoration: 'none', color: 'black', cursor: isClickable ? 'pointer' : 'default', opacity: isClickable ? 1 : 0.5}}>
      <Box>
        <Grid
          container
          sx={{
              pl: 2, pr: 1, textAlign: 'center', borderBottom: '#bdbdbd 2px solid', '&:hover': { backgroundColor: isClickable ? '#ff980015' : '' },
          }}
        >
          <Grid item xs={12} sm={2} sx={{ textAlign: 'left' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis'
            }}>
                {
                  selectedRun?.attributes.name
                }
            </p>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis'
            }}>
              <b>
                {
                  sample.attributes.sample_name
                  || sample.attributes.sample_id
                  || sample.attributes.library_id
                  || sample.attributes.id
                }
              </b>
            </p>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            { statusContent }
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            { yieldContent }
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            { speciesIDContent }
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p><small>{new Date(sample.attributes.created_at).toLocaleString()}</small></p>
          </Grid>
        </Grid>
      </Box>
    </Link>
  );
}

export default SampleInDatasetListItem;
